import { useCallback, useEffect, useState } from 'react';
import { cn } from '@discover-lib/utils';
import Button, { type ButtonProps } from '@discover-ui/button';
import UpChevronIcon from '@discover-ui/icons/chevron-up.svg';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';

export type ScrollToTopButtonProps = Omit<ButtonProps, 'round' | 'width' | 'kind' | 'onClick'>;

export default function ScrollToTopButton({ className, ...props }: ScrollToTopButtonProps) {
  const [visible, setVisible] = useState(false);
  const { track } = useAnalytics();

  const onScroll = useCallback(() => {
    const shouldShow = window.scrollY > 400;

    if (visible !== shouldShow) {
      setVisible(shouldShow);
    }
  }, [visible, setVisible]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  return (
    <Button
      round
      width="square"
      kind="normal"
      onClick={() => {
        track('button_clicked', {
          button_name: 'scroll to top',
          button_type: 'navigation',
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      className={cn(
        'z-10 shadow shadow-black',
        {
          hidden: !visible,
        },
        className
      )}
      {...props}
      aria-label="Scroll to top">
      <UpChevronIcon className="m-0.5 size-5" />
    </Button>
  );
}
